import React from 'react';
import { Modal, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { MessageTemplate } from 'src/models/messageTemplate';
import { useMessageTemplateList } from 'src/usecases/messageTemplate/reader';

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (messageTemplate: MessageTemplate) => void;
}

/**
 * テーブル列定義を作成する関数
 */
const createColumns = (uniqueCategories: string[]): ColumnsType<MessageTemplate> => [
  {
    title: 'メッセージ',
    dataIndex: ['broadCastMessage', 'text'],
    key: 'text',
    width: 500,
    ellipsis: {
        showTitle: true,
    },
    render: (text: string) => (
        <div style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            minHeight: '42px'
        }}>
            {text}
        </div>
    ),
  },
  {
    title: 'カテゴリ',
    dataIndex: 'category',
    key: 'category',
    width: 150,
    filters: uniqueCategories.map(category => ({
      text: category,
      value: category,
    })),
    onFilter: (value, record) => record.category === value,
  },
  {
    title: '作成日時',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 150,
    sorter: (a, b) => {
      const dateA = dayjs(a.createdAt);
      const dateB = dayjs(b.createdAt);
      return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
    },
    render: (date: Date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
  },
];

export const DirectMessageTemplateModal: React.FC<Props> = ({ open, onClose, onSelect }) => {
  const values = useMessageTemplateList();

  // フィルタリング処理をメモ化
  const filteredTemplates = React.useMemo(() => {
    return values.data?.messageTemplates.filter(
      (template) => template.usage === 'directMessage'
    ) ?? [];
  }, [values.data?.messageTemplates]);

  // 使用されているカテゴリを取得
  const uniqueCategories = React.useMemo(() => {
    const categories = new Set<string>();
    filteredTemplates.forEach(template => {
      if (template.category) {
        categories.add(template.category);
      }
    });
    return Array.from(categories).sort();
  }, [filteredTemplates]);

  // テーブル列定義をメモ化（カテゴリフィルターを含む）
  const columns = React.useMemo(() => createColumns(uniqueCategories), [uniqueCategories]);

  // テンプレート選択ハンドラをメモ化
  const handleSelectTemplate = React.useCallback(
    (template: MessageTemplate) => {
      onSelect(template);
      onClose();
    },
    [onSelect, onClose]
  );

  // テーブル行の設定をメモ化
  const onRow = React.useCallback(
    (record: MessageTemplate) => ({
      onClick: () => handleSelectTemplate(record),
      style: { cursor: 'pointer' },
    }),
    [handleSelectTemplate]
  );

  return (
    <Modal
      title="テンプレート選択"
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
    >
      <Table<MessageTemplate>
        columns={columns}
        dataSource={filteredTemplates}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
        }}
        onRow={onRow}
        size="middle"
        scroll={{ y: 400 }}
      />
    </Modal>
  );
};